import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import { Placeholder } from '@tiptap/extensions';
// @ts-ignore
import styles from './TiptapEditor.module.css';

interface TiptapEditorProps {
  value: string;
  onChange: (html: string, textLength: number) => void;
  placeholder?: string;
  /** Applied to the editable ProseMirror root (e.g. for <label htmlFor>). */
  editorId?: string;
  /** Read-only: no typing, toolbar hidden. */
  disabled?: boolean;
}

interface ToolbarButtonProps {
  onClick: () => void;
  isActive?: boolean;
  title: string;
  children: React.ReactNode;
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({
  onClick,
  isActive,
  title,
  children,
}) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className={`${styles.toolbarBtn} ${isActive ? styles.active : ''}`}
  >
    {children}
  </button>
);

const TiptapEditor: React.FC<TiptapEditorProps> = ({
  value,
  onChange,
  placeholder = 'Describe your property…',
  editorId,
  disabled = false,
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({ openOnClick: false }),
    ],
    content: value || '',
    editable: !disabled,
    editorProps: {
      attributes: editorId ? { id: editorId } : {},
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      const textLength = editor.getText().length;
      onChange(html, textLength);
    },
  });

  useEffect(() => {
    if (editor) editor.setEditable(!disabled);
  }, [disabled, editor]);

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value !== current) {
      editor.commands.setContent(value || '', { emitUpdate: false });
    }
  }, [value, editor]);

  if (!editor) return null;

  const handleAddLink = () => {
    const url = window.prompt('Enter URL');
    if (url) {
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: url })
        .run();
    }
  };

  const handleClearFormatting = () => {
    editor.chain().focus().clearNodes().unsetAllMarks().run();
  };

  return (
    <div className={styles.wrapper}>
      {!disabled && <div className={styles.toolbar}>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
          title="Bold"
        >
          <strong>B</strong>
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
          title="Italic"
        >
          <em>I</em>
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          isActive={editor.isActive('underline')}
          title="Underline"
        >
          <span style={{ textDecoration: 'underline' }}>U</span>
        </ToolbarButton>

        <div className={styles.divider} />

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive('bulletList')}
          title="Bullet list"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <circle cx="2" cy="3.5" r="1.2" fill="currentColor" />
            <rect x="5" y="2.8" width="8" height="1.4" rx="0.7" fill="currentColor" />
            <circle cx="2" cy="7" r="1.2" fill="currentColor" />
            <rect x="5" y="6.3" width="8" height="1.4" rx="0.7" fill="currentColor" />
            <circle cx="2" cy="10.5" r="1.2" fill="currentColor" />
            <rect x="5" y="9.8" width="8" height="1.4" rx="0.7" fill="currentColor" />
          </svg>
        </ToolbarButton>

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive('orderedList')}
          title="Numbered list"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <text x="0" y="4.5" fontSize="4.5" fill="currentColor" fontFamily="sans-serif">1.</text>
            <rect x="5" y="2.8" width="8" height="1.4" rx="0.7" fill="currentColor" />
            <text x="0" y="8" fontSize="4.5" fill="currentColor" fontFamily="sans-serif">2.</text>
            <rect x="5" y="6.3" width="8" height="1.4" rx="0.7" fill="currentColor" />
            <text x="0" y="11.5" fontSize="4.5" fill="currentColor" fontFamily="sans-serif">3.</text>
            <rect x="5" y="9.8" width="8" height="1.4" rx="0.7" fill="currentColor" />
          </svg>
        </ToolbarButton>

        <div className={styles.divider} />

        <ToolbarButton
          onClick={handleAddLink}
          isActive={editor.isActive('link')}
          title="Add link"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
          </svg>
        </ToolbarButton>

        <ToolbarButton
          onClick={handleClearFormatting}
          title="Clear formatting"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </ToolbarButton>
      </div>}

      <EditorContent editor={editor} className={styles.editorContent} />
    </div>
  );
};

export default TiptapEditor;
